import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, Grid, MenuItem, Chip, Autocomplete
} from "@mui/material";
import { Add, Search, AssignmentReturn, Book, Person, School } from "@mui/icons-material";
import { 
  collection, query, where, getDocs, addDoc, updateDoc, 
  doc, getDoc, Timestamp, orderBy 
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";

function Issuebooks() {
  const [issuedList, setIssuedList] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [bookOptions, setBookOptions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    studentId: "", studentName: "", bookId: "", bookName: "",
    barcodeNo: "", classId: "", className: "",
    issueDate: new Date().toISOString().split('T')[0], notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        const sId = userSnap.data()?.schoolId;
        setSchoolId(sId);
        if (sId) {
          fetchTransactions(sId);
          fetchStudentsAndBooks(sId);
        }
      } catch (e) { console.error(e); }
    };
    loadData();
  }, []);

  const fetchStudentsAndBooks = async (sId) => {
    // 1. Fetch Students (Joining Admission + Students)
    const admSnap = await getDocs(query(collection(db, "admission"), where("schoolId", "==", sId)));
    const sList = await Promise.all(admSnap.docs.map(async (adm) => {
      const sSnap = await getDoc(doc(db, "students", adm.data().studentId));
      const sData = sSnap.data();
      const fullName = `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase();
      return {
        id: adm.data().studentId,
        label: fullName,
        classId: adm.data().classId || "",
        className: adm.data().className || "" // Assuming className is in admission
      };
    }));
    setStudentOptions(sList);

    // 2. Fetch Books
    const bookSnap = await getDocs(query(collection(db, "books"), where("schoolId", "==", sId)));
    setBookOptions(bookSnap.docs.map(d => ({
      id: d.id,
      label: d.data().bookName,
      barcodeNo: d.data().barcodeNo || ""
    })));
  };

  const fetchTransactions = async (sId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "issued_books"), where("schoolId", "==", sId), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setIssuedList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleIssue = async () => {
    if (!formData.studentId || !formData.bookId) {
      return Swal.fire("Error", "Please select both a student and a book", "error");
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "issued_books"), {
        ...formData,
        schoolId,
        status: "Issued",
        fineAmount: 0,
        returnDate: "",
        createdAt: Timestamp.now()
      });
      
      Swal.fire("Success", "Book Issued", "success");
      setOpen(false);
      setFormData({ studentId: "", studentName: "", bookId: "", bookName: "", barcodeNo: "", classId: "", className: "", issueDate: new Date().toISOString().split('T')[0], notes: "" });
      fetchTransactions(schoolId);
    } catch (e) { Swal.fire("Error", e.message, "error"); } finally { setSubmitting(false); }
  };

  const handleReturn = async (item) => {
    const { value: fine } = await Swal.fire({
      title: 'Return Book',
      text: `Returning "${item.bookName}" from ${item.studentName}`,
      input: 'number',
      inputLabel: 'Fine Amount (₹)',
      inputValue: 0,
      showCancelButton: true,
      confirmButtonText: 'Confirm Return',
    });

    if (fine !== undefined) {
      try {
        await updateDoc(doc(db, "issued_books", item.id), {
          status: "Returned",
          returnDate: new Date().toISOString().split('T')[0],
          fineAmount: parseFloat(fine) || 0
        });
        Swal.fire("Success", "Book Returned", "success");
        fetchTransactions(schoolId);
      } catch (e) { Swal.fire("Error", e.message, "error"); }
    }
  };

  const filteredData = issuedList.filter(item => 
    item.studentName.toLowerCase().includes(search.toLowerCase()) || 
    item.bookName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={1}>
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>Library Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Issue Book</Button>
      </Stack>

      <Card sx={{ p: 1}}>
        <TextField 
          fullWidth placeholder="Search transactions..." 
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8f9fa" }}>
            <TableRow>
              <TableCell><b>Sr No</b></TableCell>
              <TableCell><b>Student</b></TableCell>
              <TableCell><b>Book</b></TableCell>
              <TableCell><b>Dates</b></TableCell>
              <TableCell><b>Fine</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="center"><b>Action</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} sx={{ m: 2 }}/></TableCell></TableRow>
            ) : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
              <TableRow key={item.id} hover>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{item.studentName}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.className}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{item.bookName}</Typography>
                  <Typography variant="caption">SN: {item.barcodeNo}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">Issued: {item.issueDate}</Typography>
                  {item.returnDate && <Typography variant="caption" display="block" color="success.main">Returned: {item.returnDate}</Typography>}
                </TableCell>
                <TableCell>₹{item.fineAmount || 0}</TableCell>
                <TableCell>
                  <Chip label={item.status} size="small" color={item.status === "Issued" ? "warning" : "success"} />
                </TableCell>
                <TableCell align="center">
                  {item.status === "Issued" && (
                    <IconButton color="primary" onClick={() => handleReturn(item)}><AssignmentReturn /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Issue New Book</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <Autocomplete
              options={studentOptions}
              onChange={(e, val) => setFormData({
                ...formData, 
                studentId: val?.id || "", 
                studentName: val?.label || "",
                classId: val?.classId || "",
                className: val?.className || ""
              })}
              renderInput={(params) => <TextField {...params} label="Select Student" />}
            />
            <Autocomplete
              options={bookOptions}
              onChange={(e, val) => setFormData({
                ...formData, 
                bookId: val?.id || "", 
                bookName: val?.label || "",
                barcodeNo: val?.barcodeNo || ""
              })}
              renderInput={(params) => <TextField {...params} label="Select Book" />}
            />
            <TextField label="Issue Date" type="date" fullWidth value={formData.issueDate} onChange={e => setFormData({...formData, issueDate: e.target.value})} InputLabelProps={{ shrink: true }} />
            <TextField label="Notes" multiline rows={2} fullWidth value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleIssue} disabled={submitting}>Issue</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Issuebooks;